import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, InputNumber, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtTime, ok, toastErr } from './common';

const { Text } = Typography;

// stored as fractions, edited as percent
const PERCENT = new Set(['hard_bounce_rate', 'complaint_rate']);
const SOURCE = { db: ['blue', '后台'], env: ['purple', '环境变量'], default: ['default', '默认'] };
const toView = (k, v) => (v === null || v === undefined || v === '' ? v : PERCENT.has(k) ? Number((v * 100).toPrecision(6)) : v);
const toStore = (k, v) => (v === null || v === undefined || v === '' ? v : PERCENT.has(k) ? Number((v / 100).toPrecision(6)) : v);

const Settings = () => {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [log, setLog] = useState({ items: [], total: 0 });
    const [logPage, setLogPage] = useState(1);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminExtService.getSettings();
            setItems(r?.data?.items || []);
            setDraft({});
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLog = useCallback(async () => {
        try {
            const r = await tmAdminExtService.adminLog({ page: logPage, per_page: 20 });
            setLog(r?.data || { items: [], total: 0 });
        } catch (e) {
            toastErr(e, '加载失败');
        }
    }, [logPage]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadLog(); }, [loadLog]);

    const save = async (values) => {
        setSaving(true);
        try {
            const r = await tmAdminExtService.saveSettings(values);
            if (ok(r)) {
                message.success(Object.keys(r.data?.changed || {}).length ? '已保存' : '没有变化');
                setItems(r.data?.items || []);
                setDraft({});
                loadLog();
            }
        } catch (e) {
            toastErr(e, '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const dirty = Object.keys(draft).length > 0;

    const columns = [
        { title: '参数', dataIndex: 'label', render: (v, s) => <span>{v}<br /><Text type="secondary" style={{ fontSize: 12 }}>{s.key}</Text></span> },
        {
            title: '值', key: 'v', width: 320,
            render: (_, s) => {
                const cur = s.key in draft ? draft[s.key] : toView(s.key, s.value);
                const set = (v) => setDraft((d) => ({ ...d, [s.key]: v }));
                if (s.type === 'url') return <Input value={cur} onChange={(e) => set(e.target.value)} style={{ width: 300 }} />;
                const min = PERCENT.has(s.key) ? s.min * 100 : s.min;
                const max = PERCENT.has(s.key) ? s.max * 100 : s.max;
                return (
                    <InputNumber value={cur} min={min} max={max} step={s.type === 'int' ? 1 : PERCENT.has(s.key) ? 0.01 : 0.5}
                        precision={s.type === 'int' ? 0 : undefined} addonAfter={PERCENT.has(s.key) ? '%' : undefined}
                        onChange={(v) => set(v)} style={{ width: 200 }} />
                );
            },
        },
        {
            title: '来源', key: 'src', width: 220,
            render: (_, s) => (
                <Space size={4} wrap>
                    <Tag color={SOURCE[s.source]?.[0]}>{SOURCE[s.source]?.[1]}</Tag>
                    <Tooltip title={s.env ? `${s.env}${s.env_value !== null ? ` = ${s.env_value}` : '（未设置）'}` : null}>
                        <Text type="secondary" style={{ fontSize: 12 }}>默认 {PERCENT.has(s.key) ? `${toView(s.key, s.default)}%` : String(s.default)}</Text>
                    </Tooltip>
                    {s.source === 'db' && (
                        <Tooltip title="恢复为环境变量 / 默认值">
                            <Button size="small" type="text" icon={<UndoOutlined />} loading={saving} onClick={() => save({ [s.key]: null })} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="邮件推送全局设置" extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={load} />
                    <Button disabled={!dirty} onClick={() => setDraft({})}>撤销</Button>
                    <Button type="primary" disabled={!dirty} loading={saving}
                        onClick={() => save(Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, toStore(k, v)])))}>保存</Button>
                </Space>
            }>
                <Table rowKey="key" size="small" loading={loading} dataSource={items} columns={columns} pagination={false} scroll={{ x: 800 }} />
            </Card>
            <Card size="small" title="管理操作记录">
                <Table rowKey="id" size="small" dataSource={log.items || []} scroll={{ x: 900 }}
                    pagination={{ current: logPage, pageSize: 20, total: log.total, onChange: setLogPage, showSizeChanger: false }}
                    columns={[
                        { title: '时间', dataIndex: 'created_at', width: 155, render: fmtTime },
                        { title: '管理员', key: 'a', width: 140, render: (_, l) => `#${l.admin_id ?? '-'} ${l.admin_name || ''}` },
                        { title: '操作', dataIndex: 'action', width: 170, render: (v) => <Tag>{v}</Tag> },
                        { title: '对象', key: 't', width: 120, render: (_, l) => `${l.target_type} #${l.target_id}` },
                        { title: '详情', dataIndex: 'detail', ellipsis: true, render: (d) => <Text type="secondary" style={{ fontSize: 12 }}>{JSON.stringify(d)}</Text> },
                    ]} />
            </Card>
        </Space>
    );
};

export default Settings;
